/**
 * The no-fit DECLARATION, frontend half (F1 / C1b-1b, A2).
 *
 * MIRROR PAIR - the Python twin is backend/routes/_category_decision_
 * vocabulary.py's THE NO-FIT DECLARATION section. Registered in CLAUDE.md's
 * mirror-pair table. Touch one, verify the other. The shared case table
 * backend/tests/fixtures/category_no_fit_parity_cases.json is what actually
 * joins them: a cross-language pair cannot be called from one test, so each
 * side asserts its own implementation against the same literals.
 *
 * WHAT THIS SIDE DOES AND DOES NOT DO. It READS a record (to decide whether
 * a line or a header is parked, and to render the request back to the
 * buyer) and it BUILDS THE BUYER'S TWO HALVES of one - the declaration and
 * the requested text. It never mints a request number, never stamps a
 * requestor and never stamps a timestamp: those are the server's three
 * facts, and a client that supplied them would be a second producer of a
 * record whose whole purpose is to be an audit trail.
 *
 * So two shapes read as present here, and that is deliberate:
 *   - the two-field declaration this file builds, on its way to the server
 *   - the five-field record the server stores and returns
 * The reader requires exactly the two buyer-authored halves, which both
 * shapes carry, so the same predicate answers "is this parked?" before and
 * after the round trip.
 *
 * NOT A DECISION SOURCE. See the Python twin's own note and CLAUDE.md.
 * `category_decision_source` describes how a category VALUE got onto a
 * line; a no-fit line has no value. `no_category_fits` must never appear in
 * that field, and CategoryDecisionsPanel's PROVENANCE_LABELS must never
 * gain a key for it - it deliberately renders nothing for an undeclared
 * token, and that behaviour is correct and unchanged.
 */

// The sibling key, on a line and on parsed_requirement alike.
export const CATEGORY_NO_FIT_KEY = 'category_no_fit';

// The one declaration value.
export const NO_CATEGORY_FITS = 'no_category_fits';

export const CATEGORY_NO_FIT_DECLARATION_VALUES = Object.freeze([NO_CATEGORY_FITS]);

export const NO_FIT_FIELD_DECLARATION = 'declaration';
export const NO_FIT_FIELD_REQUESTED_TEXT = 'requested_text';
export const NO_FIT_FIELD_REQUEST_NUMBER = 'request_number';
export const NO_FIT_FIELD_REQUESTOR = 'requestor';
export const NO_FIT_FIELD_DECLARED_AT = 'declared_at';

/**
 * THE ONE READER, mirroring read_category_no_fit. The stored record, or
 * null. Strict on BOTH buyer-authored halves for the same reason the Python
 * twin is: a record carrying the declaration but no text, or text but no
 * declaration, is a defect rather than a half-request, and admitting it
 * would park a buyer against a request the admin cannot act on.
 *
 * An unrecognised declaration value reads as null rather than passing
 * through - showing a buyer an undeclared token would present a defect as
 * a fact.
 */
export const readCategoryNoFit = (container) => {
  if (!container || typeof container !== 'object' || Array.isArray(container)) return null;
  const record = container[CATEGORY_NO_FIT_KEY];
  if (!record || typeof record !== 'object' || Array.isArray(record)) return null;
  if (!CATEGORY_NO_FIT_DECLARATION_VALUES.includes(record[NO_FIT_FIELD_DECLARATION])) return null;
  const text = record[NO_FIT_FIELD_REQUESTED_TEXT];
  if (typeof text !== 'string' || !text.trim()) return null;
  return record;
};

/** Predicate form. One implementation, so no two call sites can disagree
 *  about what counts as a declaration. */
export const hasCategoryNoFit = (container) => readCategoryNoFit(container) !== null;

/**
 * THE BUYER'S TWO HALVES, built together. The client-side counterpart of
 * build_category_no_fit_record's same guarantee: the declaration is set by
 * this function and has no parameter, so text can never travel without it.
 *
 * Returns null for blank text rather than throwing - the control that calls
 * this keeps its send button disabled until there is text, so a null here
 * means a caller bypassed that, and refusing to build is the same answer
 * the server gives (400) one hop later.
 */
export const buildCategoryNoFitDeclaration = (requestedText) => {
  const text = typeof requestedText === 'string' ? requestedText.trim() : '';
  if (!text) return null;
  return {
    [NO_FIT_FIELD_DECLARATION]: NO_CATEGORY_FITS,
    [NO_FIT_FIELD_REQUESTED_TEXT]: text,
  };
};

/**
 * The buyer-facing sentence for a parked subject, and THE ONE PLACE IT IS
 * WRITTEN (Ashok ruling: "Awaiting master update"). The line badge, the
 * picker trigger, the Intake interstitial and the forward refusal all read
 * it from here, so the four surfaces cannot drift into four wordings for
 * one state.
 */
export const AWAITING_MASTER_UPDATE = 'Awaiting master update';

/**
 * The request number off a record, or null. A record that has not yet made
 * its round trip carries no number - that is not an error, it is the two
 * seconds between the buyer pressing send and the server answering - so
 * every surface rendering the number must handle its absence.
 */
export const categoryNoFitRequestNumber = (container) => {
  const record = readCategoryNoFit(container);
  const number = record && record[NO_FIT_FIELD_REQUEST_NUMBER];
  return typeof number === 'string' && number.trim() ? number.trim() : null;
};
